import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { BackButton } from '../../../ui/BackButton'
import { Icon } from '../../../ui/Icon'
import { Failed, Loading } from '../../../ui/Load'
import { ProductCard } from '../../../ui/ProductCard'
import { useSession } from '../../../app/session'
import { useCart } from '../../cart/CartProvider'
import { useFavoriteProducts, useFavorites, useToggleFavorite, type FavoriteProduct } from '../api'

/**
 * Избранное: товары, новые сверху, с «В корзину», как в каталоге. Карточки —
 * из каталога, поэтому цена и наличие сегодняшние. Товар, который сняли с
 * витрины, остаётся строкой «Нет в продаже» — её можно только убрать: молча
 * пропавший товар выглядел бы как потерянный выбор.
 */
export function FavoritesScreen() {
  const { t, i18n } = useTranslation(['catalog', 'common'])
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()
  const favorites = useFavorites()
  const list = favorites.data ?? []
  const products = useFavoriteProducts(
    list.map((item) => item.product_id),
    i18n.language,
  )

  const open = (id: number) => {
    void navigate(`/catalog/${id}`)
  }

  return (
    <section className="screen fav-screen">
      <div className="mhead">
        <BackButton to={isAuthenticated ? '/profile' : '/'} />
        <h1>{t('catalog:favorites.title')}</h1>
      </div>

      {favorites.isPending ? (
        <Loading what={t('common:state.loading')} shape="products" count={4} />
      ) : null}
      {favorites.isError ? (
        <Failed
          error={favorites.error}
          onRetry={() => {
            void favorites.refetch()
          }}
        />
      ) : null}

      {favorites.isSuccess && list.length === 0 ? (
        <div className="card empty">
          <Icon name="i-heart" size={54} />
          <div className="t">{t('catalog:favorites.empty')}</div>
          {t('catalog:favorites.emptyHint')}
          <div className="fav-empty-action">
            <Button
              onPress={() => {
                void navigate('/catalog')
              }}
            >
              {t('common:actions.toCatalog')}
            </Button>
          </div>
        </div>
      ) : null}

      {list.length > 0 ? (
        <ul className="grid fav-grid">
          {list.map((item, index) => (
            <li key={item.product_id}>
              <FavoriteTile
                productId={item.product_id}
                card={products[index] as FavoriteProduct}
                onOpen={open}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

interface FavoriteTileProps {
  productId: number
  card: FavoriteProduct
  onOpen: (id: number) => void
}

/** Одна плитка избранного: товар, заготовка, «нет в продаже» или повтор загрузки. */
function FavoriteTile({ productId, card, onOpen }: FavoriteTileProps) {
  const { t } = useTranslation(['catalog', 'common'])
  const cart = useCart()
  const toggle = useToggleFavorite()

  if (card.product) {
    const product = card.product
    return (
      <ProductCard
        product={product}
        quantity={cart.quantityOf(product.id)}
        onOpen={onOpen}
        onAdd={() => {
          cart.changeQuantity(product.id, 1, product.maxQty)
        }}
        onChangeQuantity={(_product, delta) => {
          cart.changeQuantity(product.id, delta, product.maxQty)
        }}
      />
    )
  }

  if (card.isPending) {
    return (
      <div className="sk-pcard" role="status" aria-busy="true">
        <span className="visually-hidden">{t('common:state.loading')}</span>
        <i className="sk-b sk-img" aria-hidden="true" />
        <i className="sk-b sk-l" aria-hidden="true" />
        <i className="sk-b sk-l sk-s" aria-hidden="true" />
        <i className="sk-b sk-btn" aria-hidden="true" />
      </div>
    )
  }

  const { gone } = card
  return (
    <div className="pcard fav-gone">
      <div className="pimg" aria-hidden="true">
        <Icon name="i-box" size={44} />
      </div>
      <div className="pbody">
        <div className="pname">
          {gone ? t('catalog:favorites.gone') : t('catalog:favorites.loadFailed')}
        </div>
        {gone ? <div className="psub">{t('catalog:favorites.goneHint')}</div> : null}
        {gone ? (
          <Press
            squish="chip"
            className="padd rxonly"
            onPress={() => {
              toggle.mutate({ productId, on: false })
            }}
          >
            {t('catalog:favorites.remove')}
          </Press>
        ) : (
          <Press squish="chip" className="padd" onPress={card.retry}>
            {t('common:actions.retry')}
          </Press>
        )}
      </div>
    </div>
  )
}
